import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, ChevronDown, ChevronRight } from 'lucide-react';
import useMetafieldDefinitions from '../hooks/useMetafieldDefinitions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import SyncModal from '../components/SyncModal';

const STATUS_FILTERS = [
  { id: 'all',     label: 'All definitions' },
  { id: 'missing', label: 'Missing translations only' },
];

// A definition has "missing translations" iff translation_policy='translate'
// AND it's not fully pushed for the active locale set (DE + ES).
// Heuristic: pushed_count < distinct_count * 2 (locales). Falls back to
// pushed_count === 0 when distinct_count is missing or 0.
function isMissingTranslation(def) {
  if (def.translation_policy !== 'translate') return false;
  const distinct = def.distinct_count || 0;
  const pushed = def.pushed_count || 0;
  const translated = def.translated_count || 0;
  if (distinct > 0) return pushed < distinct * 2;
  return pushed === 0 || translated > 0;
}

// Phase 22 — table is now scoped to one policy bucket (translate or skip).
// Renders both structured and legacy/unstructured definitions in a single
// flow, with the Legacy ones grouped under a divider when present.
function DefinitionsTable({ definitions, routePrefix, onRowClick }) {
  const structured = definitions.filter((d) => !d.is_unstructured);
  const unstructured = definitions.filter((d) => d.is_unstructured);

  if (structured.length === 0 && unstructured.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        Nothing here.
      </p>
    );
  }

  const TableSlice = ({ rows, legacy }) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Namespace</TableHead>
          <TableHead>Key</TableHead>
          <TableHead>Type</TableHead>
          <TableHead className="text-right">Values</TableHead>
          <TableHead className="text-right">Pushed</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((def) => (
          <TableRow
            key={def.id}
            className="cursor-pointer"
            onClick={() => onRowClick(def.id)}
          >
            <TableCell className="flex items-center gap-2">
              {def.namespace}
              {legacy && <Badge variant="secondary">Legacy</Badge>}
            </TableCell>
            <TableCell>{def.key}</TableCell>
            <TableCell>{def.type_name}</TableCell>
            <TableCell className="text-right">{def.distinct_count ?? '—'}</TableCell>
            <TableCell className="text-right text-muted-foreground">
              {def.pushed_count > 0 ? def.pushed_count : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <>
      {structured.length > 0 && <TableSlice rows={structured} legacy={false} />}
      {unstructured.length > 0 && (
        <>
          <div className="text-xs uppercase tracking-wide text-muted-foreground border-t border-border pt-4 mt-6 mb-2">
            Unstructured / Legacy
          </div>
          <TableSlice rows={unstructured} legacy={true} />
        </>
      )}
    </>
  );
}

/**
 * Shared listing page for product / variant / collection metafield definitions.
 *
 * Phase 22: split into "Translate" (top, always-open) and "Skip" (bottom,
 * collapsed by default) sections. The previous policy filter buttons are gone
 * — the sectioning replaces them. The "Missing translations only" filter still
 * lives at the top and applies to the Translate section.
 */
export default function MetafieldDefinitionsPage({ ownerType, title, routePrefix }) {
  const navigate = useNavigate();
  const {
    definitions,
    filteredDefinitions,
    loading,
    error,
    search,
    setSearch,
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    fetchData,
  } = useMetafieldDefinitions(ownerType);

  const [statusFilter, setStatusFilter] = useState('all');
  // Skip section is collapsed by default (user instruction 2026-05-16).
  const [skipOpen, setSkipOpen] = useState(false);

  // Apply the "missing translations only" filter only to the Translate set;
  // it never makes sense for the Skip bucket.
  const translateDefs = useMemo(() => {
    let list = filteredDefinitions.filter((d) => d.translation_policy === 'translate');
    if (statusFilter === 'missing') list = list.filter(isMissingTranslation);
    return list;
  }, [filteredDefinitions, statusFilter]);

  const skipDefs = useMemo(
    () => filteredDefinitions.filter(
      (d) => d.translation_policy === 'skip' || !d.translation_policy,
    ),
    [filteredDefinitions],
  );

  const handleSyncClose = () => {
    setSyncModalOpen(false);
    fetchData();
  };

  // Empty state (no data at all)
  if (!loading && !error && definitions.length === 0) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-24">
          <Database className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-base font-medium text-foreground mb-1">
            No metafield data yet.
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            Click Sync to scan your Shopify store.
          </p>
          <Button onClick={() => setSyncModalOpen(true)}>Sync from Shopify</Button>
        </div>
        <SyncModal open={syncModalOpen} onClose={handleSyncClose} />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        <Button onClick={() => setSyncModalOpen(true)}>Sync from Shopify</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {lastSynced
          ? `Last synced: ${new Date(lastSynced).toLocaleString()}`
          : 'Never synced'}
      </p>

      {/* Search */}
      <Input
        type="text"
        placeholder="Filter by namespace, key, or type..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-3"
      />

      {/* Status filter — only applies to the Translate section */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.id}
              variant={statusFilter === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatusFilter(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="flex flex-col items-center py-12">
          <p className="text-sm text-destructive mb-4">
            Failed to load definitions. Please try again.
          </p>
          <Button variant="outline" onClick={fetchData}>
            Retry
          </Button>
        </div>
      )}

      {/* Loading state */}
      {loading && !error && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex gap-4 px-2 py-3">
              <div className="animate-pulse bg-muted h-4 rounded w-24" />
              <div className="animate-pulse bg-muted h-4 rounded w-32" />
              <div className="animate-pulse bg-muted h-4 rounded w-40" />
              <div className="animate-pulse bg-muted h-4 rounded w-12" />
            </div>
          ))}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── Translate section (top, always expanded) ── */}
          <section className="mb-10">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-semibold tracking-wide text-foreground uppercase">
                To translate
              </h2>
              <span className="text-xs text-muted-foreground">
                {translateDefs.length} definition{translateDefs.length === 1 ? '' : 's'}
              </span>
            </div>
            {translateDefs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                {statusFilter === 'missing'
                  ? 'No definitions have missing translations. Nice.'
                  : 'No definitions set to translate. Open any definition to change its policy.'}
              </p>
            ) : (
              <DefinitionsTable
                definitions={translateDefs}
                routePrefix={routePrefix}
                onRowClick={(id) => navigate(`${routePrefix}/${id}`)}
              />
            )}
          </section>

          {/* ── Skip section (bottom, collapsed by default) ── */}
          <section>
            <button
              type="button"
              onClick={() => setSkipOpen((v) => !v)}
              className="w-full flex items-baseline justify-between mb-3 hover:opacity-90 cursor-pointer"
            >
              <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase flex items-center gap-1">
                {skipOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                Skipped / unset
              </h2>
              <span className="text-xs text-muted-foreground">
                {skipDefs.length} definition{skipDefs.length === 1 ? '' : 's'}
              </span>
            </button>
            {skipOpen && (
              <DefinitionsTable
                definitions={skipDefs}
                routePrefix={routePrefix}
                onRowClick={(id) => navigate(`${routePrefix}/${id}`)}
              />
            )}
          </section>
        </>
      )}

      <SyncModal open={syncModalOpen} onClose={handleSyncClose} />
    </div>
  );
}
